import { DateTime, Settings } from 'luxon';
import type { DateTimeFormatterProps } from './types';
import { EMPTY_FIELD_SIGN } from '../forms/utils';

export function toUserTime(isoString: string | undefined, originZone: string = 'utc') {
  if (isoString) {
    const localeTime = DateTime.fromISO(isoString, { zone: originZone }).setZone(
      Settings.defaultZone,
    );
    if (localeTime.isValid) {
      return localeTime;
    }
  }
  return undefined;
}

export function getDateTimeFormatted(props: DateTimeFormatterProps) {
  // If we want to remove miliseconds completely (and not only if they are 0)
  // this regex will search until first occurrence of ".": 2023-07-24T08:24:17.7079917Z --> 2023-07-24T08:24:17
  // const regex = /^[^.]*/;
  // return props.dateTime?.toISO()?.match(regex)?.[0]

  const targetDateTime = (
    typeof props.value === 'string' ? DateTime.fromISO(props.value) : props.value
  )
    ?.set({ millisecond: 0 })
    .setZone(props.timezone ?? Settings.defaultZone);

  let response;

  switch (props.format ?? 'shortNoTimezone') {
    case 'iso': {
      response = targetDateTime?.toISO({
        suppressMilliseconds: true,
        includeOffset: false,
      });
      break;
    }
    case 'isoNoSeconds': {
      response = targetDateTime?.set({ second: 0 }).toISO({
        suppressMilliseconds: true,
        suppressSeconds: true,
        includeOffset: false,
      });
      break;
    }
    case 'isoDateOnly': {
      response = targetDateTime?.toISO()?.split('T')[0];
      break;
    }
    case 'short': {
      // locale and timezone is taken from luxon-defaults (coming from accountStore)
      response = targetDateTime?.toFormat("ccc, LLL dd yyyy HH:mm ('UTC' Z)");
      break;
    }
    case 'shortNoTimezone': {
      // locale is taken from luxon-defaults (coming from accountStore)
      response = targetDateTime?.toFormat('ccc, LLL dd yyyy HH:mm');
      break;
    }
    case 'shortDateOnly': {
      // locale is taken from luxon-defaults (coming from accountStore)
      response = targetDateTime?.toFormat('ccc, LLL dd yyyy');
      break;
    }
    case 'timeOnly': {
      response = targetDateTime?.toFormat('HH:mm');
      break;
    }
  }

  return response;
}

/**
 * Used to format "Period of Execution" column in JobOccurrenceTable.
 *
 * Formats based on whether execution spans same day or multiple days,
 * and whether specific time is set (vs all-day execution):
 *
 * | Condition                    | withTime | Format                                              | Example                                    |
 * |------------------------------|----------|-----------------------------------------------------|--------------------------------------------|
 * | Same day, specific time      | true     | ddd, MMM DD YYYY HH:mm – HH:mm                      | Mon, Mar 04 2025 06:00 – 07:00             |
 * | Same day, all day            | false    | ddd, MMM DD YYYY                                    | Mon, Mar 04 2025                           |
 * | Multi-day, specific time     | true     | ddd, MMM DD YYYY HH:mm – ddd, MMM DD YYYY HH:mm     | Mon, Mar 04 2025 09:00 – Fri, Mar 08 2025 12:00 |
 * | Multi-day, all day           | false    | ddd, MMM DD YYYY – ddd, MMM DD YYYY                 | Mon, Mar 04 2025 – Fri, Mar 08 2025        |
 */
export function formatTimePeriod(
  start: string | undefined,
  end: string | undefined,
  withTime: boolean = false,
): string {
  const startDt = toUserTime(start);
  const endDt = toUserTime(end);
  if (!startDt || !endDt) {
    return EMPTY_FIELD_SIGN;
  }

  const startDateFormatted = getDateTimeFormatted({
    value: startDt,
    format: 'shortDateOnly',
  });
  const endDateFormatted = getDateTimeFormatted({
    value: endDt,
    format: 'shortDateOnly',
  });

  const startTime = getDateTimeFormatted({
    value: startDt,
    format: 'timeOnly',
  });
  const endTime = getDateTimeFormatted({ value: endDt, format: 'timeOnly' });

  if (startDt.hasSame(endDt, 'day')) {
    // Same-day execution
    return withTime ? `${startDateFormatted} ${startTime} – ${endTime}` : `${startDateFormatted}`;
  }

  // Multi-day execution
  if (withTime) {
    return `${startDateFormatted} ${startTime} – ${endDateFormatted} ${endTime}`;
  }
  return `${startDateFormatted} – ${endDateFormatted}`;
}
